import { CSSResultGroup, html, LitElement, nothing, PropertyValues } from 'lit'
import { customElement, property, state } from 'lit/decorators.js'
import { ifDefined } from 'lit/directives/if-defined.js'
import { FILTER_WIZARD_STORE, FilterWizardStore } from 'components/discovery/filter-wizard/filter-wizard-store.js'
import { FilterStepPagination } from 'components/discovery/filter-wizard/typing/internal.js'
import { getDisplayTypeElement } from './display-types/display-types.js'
import { style } from './filter-wizard.style.js'
import { t } from '@cloudsuite/mosaic-shared/translations/translator.js'
import { SEARCH_PRODUCTS_STORE, SearchProductsStore } from 'components/discovery/shared/search/stores/search-products-store.js'
import { FilterAttributes } from 'components/discovery/shared/search/typing/filter-attributes.js'
import { FilterWizardDisplayOptions, FilterWizardConfig, FilterWizardFilters } from 'components/discovery/filter-wizard/typing/config.js'
import { hostStyle } from '@cloudsuite/mosaic-shared/styles/host.style.js'
import { inject } from '@cloudsuite/mosaic-shared/scripts/dependency-injection.js'
import { createEffect } from '@cloudsuite/mosaic-shared/scripts/signals.js'
import { initializeIconsByRole } from '@cloudsuite/mosaic-shared/icons/library.js'

initializeIconsByRole({
  'csc-filter-wizard.previous': { name: 'chevron-left', style: 'regular' },
  'csc-filter-wizard.next': { name: 'chevron-right', style: 'regular' },
})


@customElement('csc-filter-wizard')
export class CSCFilterWizard extends LitElement {
  static override styles: CSSResultGroup = [hostStyle, style]

  @property({ attribute: 'tree-path' })
  treePath = ''

  @property({ type: Object, attribute: 'initial-data' })
  initialData: unknown | null = null

  @property({ type: Object })
  configuration: unknown | null = null

  @property({ type: Object, attribute: 'display-options' })
  displayOptions: FilterWizardDisplayOptions | null = null

  @inject(FILTER_WIZARD_STORE)
  private filterWizardStore!: FilterWizardStore

  @inject(SEARCH_PRODUCTS_STORE)
  private searchStore!: SearchProductsStore

  @state()
  private filterWizardConfig!: FilterWizardConfig

  @state()
  private filterWizardFilters!: FilterWizardFilters

  @state()
  private filterAttributes!: FilterAttributes

  @state()
  private filterStepPagination!: FilterStepPagination

  constructor() {
    super()

    createEffect(() => {
      this.filterWizardConfig = this.filterWizardStore.filterWizardConfig()
      this.filterWizardFilters = this.filterWizardStore.filterWizardFilters()
      this.filterAttributes = this.filterWizardStore.attributes()
      this.filterStepPagination = this.filterWizardStore.filterStepPagination()
    })
  }

  override connectedCallback() {
    super.connectedCallback()

    this.filterWizardStore.initializeFilterWizard(this.treePath, this.configuration)

    if (this.initialData) {
      this.searchStore.initializeStoreWithProducts(this.initialData)
    }
  }

  override firstUpdated() {
    this.filterWizardStore.emitCurrentFilterEvent()
  }

  protected override willUpdate(changedProperties: PropertyValues<this>): void {
    if (changedProperties.has('configuration') && changedProperties.get('configuration') !== undefined) {
      this.filterWizardStore.initializeFilterWizard(this.treePath, this.configuration)
      this.filterWizardStore.emitCurrentFilterEvent()
    }
  }

  private renderButtonIcon(icon: string | undefined, role: string, position: string) {
    if (!icon) return nothing
    return html`<cse-icon icon-role="${role}" slot="${position}"></cse-icon>`
  }

  private getNavigationButtonsConfig() {
    const configNav = this.filterWizardConfig.navigationButtons
    const displayNav = this.displayOptions?.navigationButtons

    return {
      previousVariant: displayNav?.previousVariant ?? configNav?.previousVariant ?? 'primary',
      nextVariant: displayNav?.nextVariant ?? configNav?.nextVariant ?? 'primary',
      previousIcon: displayNav?.previousIcon ?? configNav?.previousIcon,
      nextIcon: displayNav?.nextIcon ?? configNav?.nextIcon,
    }
  }

  private getImageDisplayConfig() {
    const configImage = this.filterWizardConfig.imageDisplay
    const displayImage = this.displayOptions?.imageDisplay

    if (!displayImage && !configImage) return undefined

    const height = displayImage?.height ?? configImage?.height
    const width = displayImage?.width ?? configImage?.width
    const fit = displayImage?.fit ?? configImage?.fit

    return {
      ...(height !== undefined && { height }),
      ...(width !== undefined && { width }),
      ...(fit !== undefined && { fit }),
    }
  }

  private getPaperConfig() {
    return this.displayOptions?.paper
  }

  override render() {
    const filter = this.filterWizardFilters[this.filterStepPagination.activeStep]
    if (!filter) {
      return nothing
    }

    const attributeValues = filter.attribute.valueIds.map(valueId =>
      this.filterAttributes.getAttributeValue(filter.attribute.id, valueId)
    )

    const imageConfig = this.getImageDisplayConfig()
    const navConfig = this.getNavigationButtonsConfig()
    const paperConfig = this.getPaperConfig()

    const displayTypeElement = getDisplayTypeElement(filter.displayType, imageConfig)

    return html`
      <cse-paper
        id="filter-${filter.attribute.id}"
        class="csc-filter-wizard"
        variant="${ifDefined(paperConfig?.variant)}"
        size="${ifDefined(paperConfig?.size)}"
        ?bordered="${paperConfig?.bordered}"
      >
        <cse-text variant="heading-3" class="filter-name">
          ${filter.name}
        </cse-text>

        <div class="filter-options">
          ${displayTypeElement(attributeValues)}
        </div>

        <cse-group justify="space-between">
          <cse-button
            variant="${navConfig.previousVariant}"
            id="previous-button"
            ?disabled="${!this.filterStepPagination.hasPrevious}"
            @click="${this.filterWizardStore.previousStep}"
          >
            ${this.renderButtonIcon(navConfig.previousIcon, 'csc-filter-wizard.previous', 'prefix')}
            ${t('previous_filters_step_button', { ns: 'csc-filter-wizard', default: 'Previous' })}
          </cse-button>
          <cse-button
            variant="${navConfig.nextVariant}"
            id="next-button"
            ?disabled="${!this.filterStepPagination.hasNext}"
            @click="${this.filterWizardStore.nextStep}"
          >
            ${t('next_filters_step_button', { ns: 'csc-filter-wizard', default: 'Next' })}
            ${this.renderButtonIcon(navConfig.nextIcon, 'csc-filter-wizard.next', 'suffix')}
          </cse-button>
        </cse-group>
      </cse-paper>
    `
  }
}
